import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
    LayoutDashboard,
    Package,
    History,
    Settings,
    LogOut,
    Menu,
    X,
    UserX,
    TrendingUp,
    Users,
    UserCircle,
    Truck,
    Building2,
    Calculator,
    ShoppingBag,
    Zap,
    MessageCircle,
    Inbox
} from 'lucide-react';

const getNavItems = (user) => {
    const items = [];
    
    // Tableau de Bord - Admin and Super Admin only (not vendors, not confirmateur)
    if (user?.role === 'admin' || user?.role === 'super_admin') {
        items.push({ path: '/', label: 'Tableau de Bord', icon: LayoutDashboard });
    }
    
    // Commandes - All users except confirmateur goes first to orders
    items.push({ path: '/commandes', label: 'Commandes', icon: Package });
    
    // Messages/Chat - All users can access (placed high for visibility)
    items.push({ path: '/messages', label: 'Messages', icon: MessageCircle });
    
    // Social Inbox - Admin and Super Admin only
    if (user?.role === 'admin' || user?.role === 'super_admin') {
        items.push({ path: '/social-inbox', label: 'Inbox Social', icon: Inbox });
    }
    
    // Produits - Admin and Super Admin (NOT vendors, NOT confirmateur)
    if (user?.role === 'admin' || user?.role === 'super_admin') {
        items.push({ path: '/produits', label: 'Produits', icon: ShoppingBag });
    }
    
    // Produits Vendeurs (Admin) - Manage & Sync to Shopify
    if (user?.role === 'admin' || user?.role === 'super_admin') {
        items.push({ path: '/produits-vendeurs', label: 'Produits Vendeurs', icon: Package });
    }
    
    // Mes Produits - Vendors only (their own product catalog)
    if (user?.role === 'vendeur') {
        items.push({ path: '/mes-produits', label: 'Mes Produits', icon: ShoppingBag });
    }
    
    // Clients - Admin, office_agent, confirmateur, super_admin (NOT vendors)
    if (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'office_agent' || user?.role === 'confirmateur') {
        items.push({ path: '/clients', label: 'Clients', icon: UserCircle });
    }
    
    // Vendeurs - Admin, Super Admin, and Confirmateur
    if (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'confirmateur') {
        items.push({ path: '/vendeurs', label: 'Vendeurs', icon: Users });
    }
    
    // Livraisons - Drivers, office agents, confirmateur, admin, super_admin (NOT vendors)
    if (user?.role === 'admin' || user?.role === 'driver' || user?.role === 'office_agent' || user?.role === 'confirmateur' || user?.role === 'super_admin') {
        items.push({ path: '/livraisons', label: 'Livraisons', icon: Truck });
    }
    
    // Abandonnées - Admin and super_admin (NOT vendors, NOT confirmateur)
    if (user?.role === 'admin' || user?.role === 'super_admin' || user?.permissions?.includes('view_abandoned')) {
        items.push({ path: '/abandonnees', label: 'Abandonnées', icon: UserX });
    }
    
    // Rapports - Admin and super_admin (NOT vendors, NOT confirmateur)
    if (user?.role === 'admin' || user?.role === 'super_admin' || user?.permissions?.includes('view_reports')) {
        items.push({ path: '/rapports', label: 'Rapports', icon: TrendingUp });
    }
    
    // Commissions - Admin, super_admin, vendeurs, office_agent (NOT confirmateur - no financial access)
    if (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'vendeur' || user?.role === 'office_agent') {
        items.push({ path: '/commissions', label: 'Commissions', icon: Calculator });
    }
    
    // Historique - Admin and Super Admin only
    if (user?.role === 'admin' || user?.role === 'super_admin') {
        items.push({ path: '/historique', label: 'Historique', icon: History });
    }
    
    // Automatisations SMS - Admin and Super Admin only
    if (user?.role === 'admin' || user?.role === 'super_admin') {
        items.push({ path: '/automatisations', label: 'Automatisations', icon: Zap });
    }
    
    // Utilisateurs - Admin and super_admin (for office agents, drivers, etc.)
    if (user?.role === 'admin' || user?.role === 'super_admin') {
        items.push({ path: '/utilisateurs', label: 'Utilisateurs', icon: UserCircle });
    }
    
    // Super Admin Dashboard - Super Admin only (NEW - Multi-tenant management)
    if (user?.role === 'super_admin') {
        items.push({ path: '/super-admin', label: '👑 Super Admin', icon: Building2 });
    }
    
    // Paramètres - All users
    items.push({ path: '/parametres', label: 'Paramètres', icon: Settings });
    
    return items;
};

export const Layout = ({ children }) => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/connexion');
    };

    return (
        <div className="app-layout">
            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div
                    className="sidebar-overlay lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                    data-testid="sidebar-overlay"
                />
            )}

            {/* Sidebar */}
            <aside className={cn('sidebar', sidebarOpen && 'open')} data-testid="sidebar">
                <div className="sidebar-header">
                    <div className="flex items-center justify-between">
                        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                            <img src="/bbm-logo.png" alt="BBM" className="w-10 h-10 rounded-[2px] object-cover" />
                            <span className="text-lg font-semibold tracking-tight">BBM</span>
                        </Link>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="lg:hidden"
                            onClick={() => setSidebarOpen(false)}
                            data-testid="close-sidebar-btn"
                        >
                            <X className="w-5 h-5" />
                        </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Gestion de Commandes</p>
                </div>

                <nav className="sidebar-nav" data-testid="sidebar-nav">
                    <ul className="space-y-1">
                        {getNavItems(user).map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname === item.path;
                            return (
                                <li key={item.path}>
                                    <Link
                                        to={item.path}
                                        className={cn('sidebar-item', isActive && 'active')}
                                        onClick={() => setSidebarOpen(false)}
                                        data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                                    >
                                        <Icon className="w-5 h-5" />
                                        <span>{item.label}</span>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </nav>

                <div className="sidebar-footer">
                    <div className="flex items-center gap-3 mb-4 px-2">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-primary font-medium">
                                {user?.name?.charAt(0).toUpperCase()}
                            </span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{user?.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        className="w-full justify-start text-muted-foreground hover:text-destructive"
                        onClick={handleLogout}
                        data-testid="logout-btn"
                    >
                        <LogOut className="w-5 h-5 mr-3" />
                        Déconnexion
                    </Button>
                </div>
            </aside>

            {/* Main content */}
            <main className="flex-1 flex flex-col min-h-screen overflow-x-hidden max-w-full lg:max-w-none">
                {/* Mobile header */}
                <header className="lg:hidden flex items-center justify-between p-4 border-b border-border bg-card">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSidebarOpen(true)}
                        data-testid="open-sidebar-btn"
                    >
                        <Menu className="w-6 h-6" />
                    </Button>
                    <Link to="/" className="flex items-center gap-2">
                        <img src="/bbm-logo.png" alt="BBM" className="w-8 h-8 rounded-[2px] object-cover" />
                        <span className="font-semibold">BBM</span>
                    </Link>
                    <div className="w-10" /> {/* Spacer for alignment */}
                </header>

                <div className="main-content fade-in overflow-x-hidden">
                    {children}
                </div>
            </main>
        </div>
    );
};
